/**
 * Reusable batch score entry grid. Students on the left, a single
 * numeric input per row on the right, "Save all" at the bottom.
 *
 * Used by AssignmentPage (Day 5). Will be reused by ExamPage Day 6
 * (with section columns) and any later batch-score surface.
 */
import { GRADE_STYLES, gradeLetter } from "../lib/grade";
import { useEffect, useState } from "react";

import type { Student } from "../api/school";

export interface ScoreRow {
  student_id: string;
  score: number | null;
  teacher_note?: string;
}

interface ScoreEntryGridProps {
  students: Student[];
  maxScore: number;
  /** Existing scores keyed by student_id — pre-fills the inputs for editing. */
  initialScores?: Record<string, number | null>;
  onSave: (scores: ScoreRow[]) => Promise<void>;
  /** Brief save-button label override. */
  saveLabel?: string;
  /** Disable the inputs (e.g., during in-flight save). */
  disabled?: boolean;
}

export function ScoreEntryGrid({
  students,
  maxScore,
  initialScores = {},
  onSave,
  saveLabel = "保存全部",
  disabled = false,
}: ScoreEntryGridProps) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      students.map((s) => [
        s.id,
        initialScores[s.id] != null ? String(initialScores[s.id]) : "",
      ]),
    ),
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Re-sync if the roster or initial scores change (e.g., switching assignments).
  useEffect(() => {
    setValues(
      Object.fromEntries(
        students.map((s) => [
          s.id,
          initialScores[s.id] != null ? String(initialScores[s.id]) : "",
        ]),
      ),
    );
  }, [students, initialScores]);

  async function handleSave() {
    setErr(null);
    setSaving(true);
    try {
      const rows: ScoreRow[] = students
        .map((s) => {
          const raw = values[s.id]?.trim() ?? "";
          if (raw === "") return { student_id: s.id, score: null };
          const num = Number(raw);
          if (Number.isNaN(num)) throw new Error(`${s.name_zh} 的分数不是数字`);
          if (num < 0 || num > maxScore)
            throw new Error(`${s.name_zh} 的分数 ${num} 超出 0–${maxScore} 范围`);
          return { student_id: s.id, score: num };
        })
        .filter((r) => r.score !== null);
      if (rows.length === 0) throw new Error("没有可保存的分数 — 至少填入一个学生");
      await onSave(rows);
      setSavedAt(Date.now());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium w-40">学生</th>
            <th className="text-left px-4 py-2 font-medium w-32">英文名</th>
            <th className="text-left px-4 py-2 font-medium">
              分数（满分 {maxScore}）
            </th>
            <th className="text-left px-4 py-2 font-medium w-24">等第</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.id} className="border-t border-slate-100">
              <td className="px-4 py-2 text-slate-800">{s.name_zh}</td>
              <td className="px-4 py-2 text-slate-600">{s.name_en ?? "—"}</td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={maxScore}
                  step="0.1"
                  className="w-28 px-2 py-1 rounded border border-slate-200 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-200 disabled:bg-slate-100"
                  value={values[s.id] ?? ""}
                  disabled={disabled || saving}
                  placeholder="未录入"
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [s.id]: e.target.value }))
                  }
                />
              </td>
              <td className="px-4 py-2">
                {(() => {
                  const raw = values[s.id]?.trim() ?? "";
                  const g = raw === "" ? null : gradeLetter(Number(raw), maxScore);
                  return g ? (
                    <span className={`inline-block rounded border px-1.5 py-0.5 text-xs font-medium ${GRADE_STYLES[g]}`} title="A ≥85% · B ≥70% · C ≥60% · D <60%">
                      {g}
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  );
                })()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {err ? (
            <span className="text-red-600">{err}</span>
          ) : savedAt ? (
            <span className="text-emerald-600">
              已保存 · 学生雷达图将在刷新后更新
            </span>
          ) : (
            <span>留空表示该学生未录入</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled || saving}
          className="px-4 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
        >
          {saving ? "保存中…" : saveLabel}
        </button>
      </div>
    </div>
  );
}
